import { useState, useEffect } from 'react'
import { X, Mail } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import { NewsletterWidget } from './NewsletterWidget'

interface NewsletterModalProps {
  excludePages?: string[]
  currentPage?: string
}

const STORAGE_KEY = 'nl_modal_shown'
const SUBSCRIBED_KEY = 'nl_subscribed'
const TRIGGER_DELAY_MS = 20000

export function NewsletterModal({ excludePages = [], currentPage }: NewsletterModalProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (excludePages.includes(currentPage ?? '')) return
    if (sessionStorage.getItem(STORAGE_KEY)) return
    if (localStorage.getItem(SUBSCRIBED_KEY)) return

    let armed = false

    const show = () => {
      if (armed) return
      armed = true
      sessionStorage.setItem(STORAGE_KEY, '1')
      setOpen(true)
    }

    // Exit-intent uniquement (souris qui quitte la fenêtre vers le haut).
    // Le déclenchement au changement d'onglet (visibilitychange) était trop
    // agressif : la popup surgissait au simple retour sur l'onglet.
    const onMouseLeave = (e: MouseEvent) => {
      if (e.clientY < 0) show()
    }

    const timer = window.setTimeout(() => {
      document.addEventListener('mouseleave', onMouseLeave)
    }, TRIGGER_DELAY_MS)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mouseleave', onMouseLeave)
    }
  }, [currentPage])

  // Modal ouvert : fermeture à la touche Échap et blocage du scroll de fond
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Invitation à la newsletter"
        >
          {/* Overlay */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
          />

          {/* Panneau */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 z-10"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="text-jepg-blue" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Avant de partir…</h2>
                <p className="text-sm text-gray-500">Rejoignez plus de 500 professionnels RH</p>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              Recevez nos articles, conseils RH et actualités du marché de l'emploi camerounais — sans spam, une fois par semaine au plus.
            </p>

            <NewsletterWidget
              variant="onLight"
              onSuccess={() => setTimeout(() => setOpen(false), 2000)}
            />

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full mt-3 text-xs text-gray-400 hover:text-gray-500 transition-colors py-1"
            >
              Non merci, je ne suis pas intéressé(e)
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
